import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { EnvelopeIcon, UserIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
    useCreateUser,
    useDeleteUser,
    useSendPasswordReset,
    useUpdateUser
} from '../../../hooks/useUsers';
import { useToast } from '../../../providers/ToastProvider';
import type { UserListItem } from '../../../services/users';
import { apiErrorMessage } from '../../../utils/apiError';
import { formatRelativeActivity } from '../../../utils/dates';
import { useEscapeKey } from '../../../hooks/useEscapeKey';
import { OrthodontistMultiSelect } from '../../shared/OrthodontistMultiSelect';
import { PracticeMultiSelect } from '../../shared/PracticeMultiSelect';
import { DiscardUserDrawerModal } from './DiscardUserDrawerModal';
import { UDField } from './UDField';
import { UDInput } from './UDInput';
import { UDToggle } from './UDToggle';
import { UserAvatar } from './UserAvatar';
import { UserDeleteZone } from './UserDeleteZone';
import { UserNotificationSettingsSection } from './UserNotificationSettingsSection';
import { useUserDrawerForm } from './useUserDrawerForm';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface UserDrawerProps {
    mode: 'create' | 'edit';
    user: UserListItem | null;
    onClose: () => void;
    /** Opens the danger zone straight into its confirm step - used by the
     *  row menu's "Delete user" action. */
    initialConfirmDelete?: boolean;
}

// One drawer layout for create and edit, matching the design source's
// UserDrawer - only the email lock, password row, and danger zone differ
// between modes.
export function UserDrawer({ mode, user, onClose, initialConfirmDelete = false }: UserDrawerProps) {
    const isEdit = mode === 'edit';
    const navigate = useNavigate();
    const { showToast } = useToast();
    const { state, setState, isDirty } = useUserDrawerForm(isEdit ? user : null);
    const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
    // Set when the pending discard confirm was triggered by clicking a linked
    // practice row rather than by a close action - lets one modal serve both
    // "discard and close" and "discard and go to the practice" outcomes.
    const [pendingPracticeId, setPendingPracticeId] = useState<string | null>(null);
    const fullNameInputRef = useRef<HTMLInputElement>(null);

    const createUser = useCreateUser();
    const updateUser = useUpdateUser();
    const deleteUser = useDeleteUser();
    const sendPasswordReset = useSendPasswordReset();

    const isSaving = createUser.isPending || updateUser.isPending;

    const requestClose = useCallback(() => {
        if (isDirty) {
            setPendingPracticeId(null);
            setShowDiscardConfirm(true);
            return;
        }
        onClose();
    }, [isDirty, onClose]);

    // Passed to PracticeLinkSelect so clicking a linked practice row jumps to
    // its own page without silently dropping an in-progress edit.
    const requestNavigateToPractice = (practiceId: string) => {
        if (isDirty) {
            setPendingPracticeId(practiceId);
            setShowDiscardConfirm(true);
            return;
        }
        navigate(`/accounts/practices/${practiceId}`);
    };

    const handleKeepEditing = () => {
        setShowDiscardConfirm(false);
        setPendingPracticeId(null);
    };

    const handleDiscard = () => {
        setShowDiscardConfirm(false);
        if (pendingPracticeId) {
            const practiceId = pendingPracticeId;
            setPendingPracticeId(null);
            navigate(`/accounts/practices/${practiceId}`);
            return;
        }
        onClose();
    };

    const isEmailValid = EMAIL_RE.test(state.email.trim());
    const canSubmit = isEdit
        ? isDirty && state.fullname.trim().length > 0
        : state.fullname.trim().length > 0 && isEmailValid;

    const handleSubmit = async () => {
        if (!canSubmit) return;
        try {
            if (isEdit && user) {
                await updateUser.mutateAsync({
                    email: user.email,
                    patch: {
                        fullname: state.fullname.trim(),
                        practices: state.practices.map((practice) => practice.id)
                    }
                });
                showToast('User updated', 'success');
            } else {
                await createUser.mutateAsync({
                    input: {
                        email: state.email.trim(),
                        fullname: state.fullname.trim(),
                        practices: state.practices.map((practice) => practice.id)
                    },
                    sendPasswordReset: state.sendPasswordSetupEmail
                });
                showToast('User created', 'success');
            }
            onClose();
        } catch (error) {
            showToast(
                apiErrorMessage(error, isEdit ? 'Failed to save user' : 'Failed to create user'),
                'error'
            );
        }
    };

    const handleSendPasswordReset = async () => {
        if (!user) return;
        try {
            await sendPasswordReset.mutateAsync(user.email);
            showToast(`Password reset email sent to ${user.email}`, 'success');
        } catch (error) {
            showToast(apiErrorMessage(error, 'Failed to send password reset email'), 'error');
        }
    };

    const handleDelete = async () => {
        if (!user) return;
        try {
            const result = await deleteUser.mutateAsync(user.email);
            if (result.wordpressCleanupFailed) {
                showToast(
                    `${user.fullname} was removed, but the login account may need manual cleanup`,
                    'error'
                );
            } else {
                showToast(`${user.fullname} was removed`, 'success');
            }
            onClose();
        } catch (error) {
            showToast(apiErrorMessage(error, 'Failed to delete user'), 'error');
        }
    };

    const lastActivityLabel = user ? formatRelativeActivity(user.lastActivityAt) : null;

    // Escape routes through the same dirty-guard close path as the header's X
    // and the footer's Cancel button - an in-progress edit isn't lost just
    // because it was closed with the keyboard instead of a click.
    //
    // Suspended while the discard confirm is up: that modal owns Escape for the
    // top layer. Without this guard both listeners sit on `document` at once and
    // the outcome depends on their registration order, which flips whenever this
    // effect re-subscribes on a new `requestClose` identity - and in the flipped
    // order Escape closes the confirm only for this handler to reopen it.
    useEscapeKey(requestClose, !showDiscardConfirm);

    // Create mode already autoFocuses the Full name field (see its UDInput
    // below); edit mode has no autofocused field, so this fills that gap by
    // focusing the same field programmatically once the drawer mounts.
    useEffect(() => {
        if (isEdit) fullNameInputRef.current?.focus();
    }, [isEdit]);

    // Returns focus to whatever triggered the drawer (an "Add user" button, a
    // table row's menu item, ...) once it closes, so keyboard users aren't
    // dropped back at the top of the page.
    useEffect(() => {
        const previouslyFocused = document.activeElement as HTMLElement | null;
        return () => previouslyFocused?.focus();
    }, []);

    return (
        <>
            {/* Side panels keep click-out. requestClose still runs the dirty guard,
                so an edited form raises the discard confirm rather than closing. */}
            <div className="fixed inset-0 bg-black bg-opacity-20 z-40" onClick={requestClose} />
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="user-drawer-title"
                className="fixed right-0 top-0 h-full w-[480px] bg-white border-l border-gray-200 shadow-xl z-50 flex flex-col"
            >
                <div className="px-6 py-4 border-b border-gray-200 flex items-center gap-3.5 shrink-0">
                    {isEdit && user ? (
                        <UserAvatar name={user.fullname} size={40} />
                    ) : (
                        <span className="w-10 h-10 rounded-full border border-dashed border-gray-300 flex items-center justify-center text-gray-400 shrink-0">
                            <UserIcon className="h-4 w-4" />
                        </span>
                    )}
                    <div className="min-w-0 flex-1">
                        <h2
                            id="user-drawer-title"
                            className="text-lg font-bold text-gray-900 truncate"
                        >
                            {isEdit && user ? user.fullname : 'New user'}
                        </h2>
                        <p className="text-[12px] text-gray-500 truncate">
                            {isEdit && user
                                ? user.email
                                : "They'll be emailed a link to set their password"}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={requestClose}
                        className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 shrink-0"
                        aria-label="Close"
                    >
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                </div>

                <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 flex flex-col gap-5">
                    <UDField label="Full name">
                        <UDInput
                            ref={fullNameInputRef}
                            value={state.fullname}
                            onChange={(value) => setState((prev) => ({ ...prev, fullname: value }))}
                            placeholder="First and last name"
                            autoFocus={!isEdit}
                        />
                    </UDField>

                    <UDField
                        label="Email"
                        hint={isEdit ? "Email is the login and can't be changed." : undefined}
                    >
                        <UDInput
                            value={state.email}
                            onChange={(value) => setState((prev) => ({ ...prev, email: value }))}
                            placeholder="name@practice.com"
                            locked={isEdit}
                        />
                    </UDField>

                    <div className="border-t border-gray-100 pt-5">
                        <UDField label="Linked practices" count={state.practices.length}>
                            <PracticeMultiSelect
                                selected={state.practices}
                                onChange={(next) =>
                                    setState((prev) => ({ ...prev, practices: next }))
                                }
                                onNavigate={requestNavigateToPractice}
                                placeholder="Search a practice to link"
                                emptyText="No practices linked. ODO staff don't need any."
                            />
                        </UDField>
                    </div>

                    {isEdit && user && (
                        <div className="border-t border-gray-100 pt-5">
                            <UDField label="Orthodontist organizations">
                                <OrthodontistMultiSelect
                                    userId={user.id}
                                    userName={user.fullname}
                                />
                            </UDField>
                        </div>
                    )}

                    <div className="border-t border-gray-100 pt-5">
                        {isEdit && user ? (
                            <div className="flex items-center justify-between gap-4">
                                <div className="min-w-0">
                                    <span className="block text-[13px] font-semibold text-gray-800">
                                        Password
                                    </span>
                                    <span className="block text-[12px] text-gray-500">
                                        {lastActivityLabel
                                            ? `Last signed in ${lastActivityLabel}.`
                                            : 'Never signed in.'}
                                    </span>
                                </div>
                                <button
                                    type="button"
                                    disabled={sendPasswordReset.isPending}
                                    onClick={handleSendPasswordReset}
                                    className="bg-white text-gray-800 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-sm inline-flex items-center gap-2 disabled:opacity-50 shrink-0"
                                >
                                    <EnvelopeIcon className="h-4 w-4 text-gray-500" />
                                    {sendPasswordReset.isPending
                                        ? 'Sending…'
                                        : 'Send password reset email'}
                                </button>
                            </div>
                        ) : (
                            <UDToggle
                                on={state.sendPasswordSetupEmail}
                                onChange={(on) =>
                                    setState((prev) => ({ ...prev, sendPasswordSetupEmail: on }))
                                }
                                title="Send a password-setup email"
                                desc="Turn this off to create the account without notifying them yet."
                            />
                        )}
                    </div>

                    {isEdit && user && (
                        <div className="border-t border-gray-100 pt-5">
                            <UDField label="Notifications">
                                <UserNotificationSettingsSection userId={user.id} />
                            </UDField>
                        </div>
                    )}

                    {isEdit && user && (
                        <div className="mt-auto pt-5">
                            <UserDeleteZone
                                userName={user.fullname}
                                isDeleting={deleteUser.isPending}
                                onConfirmDelete={handleDelete}
                                initialConfirming={initialConfirmDelete}
                            />
                        </div>
                    )}
                </div>

                <div className="px-6 py-4 border-t border-gray-200 bg-white flex items-center justify-end gap-2.5 shrink-0">
                    <button
                        type="button"
                        onClick={requestClose}
                        className="text-sm font-semibold text-gray-500 hover:text-gray-900 px-3 h-10"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        disabled={!canSubmit || isSaving}
                        onClick={handleSubmit}
                        className="bg-black text-white font-semibold px-5 h-10 rounded-xl text-sm hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isSaving ? 'Saving…' : isEdit ? 'Save changes' : 'Create user'}
                    </button>
                </div>
            </div>

            {showDiscardConfirm && (
                <DiscardUserDrawerModal
                    onKeepEditing={handleKeepEditing}
                    onDiscard={handleDiscard}
                />
            )}
        </>
    );
}
